/**
 * KontaktDetail — HubSpot-Style Kontakt-Detailansicht
 * 
 * Features:
 * - Vollbild auf Mobile, Panel auf Desktop
 * - Tabs: Übersicht, Timeline, E-Mails, Firmen, Dokumente, Notizen
 * - Aktivitäts-Timeline mit chronologischer Darstellung
 * - Firmenzuordnung (mehrere Firmen pro Kontakt)
 * - Schnellaktionen (Anrufen, E-Mail, Aufgabe, Notiz)
 * - Kontaktdaten bearbeiten
 */

import { useState, useEffect, useCallback } from 'react';
import {
  Users, Phone, Mail, Building2, Clock, Star, X, ChevronLeft,
  FileText, Send, Edit, Calendar, MapPin, Globe, Linkedin,
  MessageSquare, Briefcase, ArrowUpRight, ArrowDownLeft,
  Plus, MoreHorizontal, Loader2, CheckCircle2, AlertCircle,
  Activity, Tag, Cake, Languages, TrendingUp, Pencil, Save,
  Folder, ChevronRight,
} from 'lucide-react';
import {
  type CRMKontakt, type CRMFirma, type CRMAktivitaet, type CRMKommunikation,
  type CRMKontaktBeteiligung,
  useCRMKommunikation, useCRMFirmen, useCRMAktivitaeten, useCRMKontaktBeteiligungen,
} from '@/hooks/useCRM';
import { useGmail, type GmailMessage } from '@/hooks/useGmail';
import { useLocation } from 'wouter';
import { aktenIdentifier, buildAkteHref } from '@/lib/akten-link';
import { toast } from 'sonner';
import LabelPicker from '@/components/kontakte/LabelPicker';
import BetriebePicker from '@/components/kontakte/BetriebePicker';
import type { KontaktLabel, BetriebSchluessel } from '@/lib/kontakt-labels';

// ============================================================
// HELPERS
// ============================================================

function formatDate(dateStr: string): string {
  return new Date(dateStr).toLocaleDateString('de-DE', { day: '2-digit', month: '2-digit', year: 'numeric' });
}

function formatDateTime(dateStr: string): string {
  const d = new Date(dateStr);
  return d.toLocaleDateString('de-DE', { day: '2-digit', month: '2-digit', year: 'numeric' }) + ' ' +
    d.toLocaleTimeString('de-DE', { hour: '2-digit', minute: '2-digit' });
}

function getInitials(name: string): string {
  return name.split(' ').map(n => n[0]).join('').slice(0, 2).toUpperCase();
}

function timeAgo(dateStr: string): string {
  const diff = Date.now() - new Date(dateStr).getTime();
  const mins = Math.floor(diff / 60000);
  if (mins < 60) return `vor ${mins}m`;
  const hours = Math.floor(mins / 60);
  if (hours < 24) return `vor ${hours}h`;
  const days = Math.floor(hours / 24);
  if (days < 30) return `vor ${days}d`;
  const months = Math.floor(days / 30);
  return `vor ${months} Mon.`;
}

const ACTIVITY_ICONS: Record<string, { icon: typeof Mail; color: string; bg: string }> = {
  email: { icon: Mail, color: '#7C3AED', bg: '#F5F3FF' },
  anruf: { icon: Phone, color: '#2563EB', bg: '#EFF6FF' },
  termin: { icon: Calendar, color: '#059669', bg: '#ECFDF5' },
  notiz: { icon: MessageSquare, color: '#D97706', bg: '#FFFBEB' },
  akte: { icon: Briefcase, color: '#DC2626', bg: '#FEF2F2' },
  rechnung: { icon: TrendingUp, color: '#0D9488', bg: '#F0FDFA' },
  dokument: { icon: FileText, color: '#6366F1', bg: '#EEF2FF' },
  marketing: { icon: Send, color: '#EC4899', bg: '#FDF2F8' },
};

const STATUS_CONFIG: Record<string, { label: string; color: string; bg: string }> = {
  aktiv: { label: 'Aktiv', color: '#059669', bg: '#ECFDF5' },
  inaktiv: { label: 'Inaktiv', color: '#6B7280', bg: '#F3F4F6' },
  lead: { label: 'Lead', color: '#2563EB', bg: '#EFF6FF' },
  kunde: { label: 'Kunde', color: '#7C3AED', bg: '#F5F3FF' },
};

type DetailTab = 'uebersicht' | 'timeline' | 'beteiligungen' | 'emails' | 'firmen' | 'dokumente' | 'notizen';

// Business-Unit-Labels für die Gruppierung der Beteiligungen (Issue #337)
const BUSINESS_UNIT_LABEL: Record<string, string> = {
  gutachten: 'Gutachten',
  mietwagen: 'Mietwagen',
  autoglas: 'Autoglas',
  werkstatt: 'Werkstatt',
  unfallgiganten: 'Unfallgiganten',
  recht: 'Recht',
  gutachter_koop: 'Gutachter Koop',
  protex: 'Protex',
  airbnb: 'Airbnb',
};

const ROLLE_LABEL: Record<string, string> = {
  kunde: 'Kunde',
  anspruchsteller: 'Anspruchsteller',
  fahrer: 'Fahrer',
  halter: 'Halter',
  gegner: 'Gegner',
  anwalt: 'Anwalt',
  werkstatt: 'Werkstatt',
  versicherung: 'Versicherung',
  mietwagen: 'Mietwagen',
  beteiligter: 'Beteiligter',
  sonstige: 'Sonstige',
};

function beteiligungenNachBetrieb(
  list: CRMKontaktBeteiligung[],
): Array<{ business_unit: string; items: CRMKontaktBeteiligung[] }> {
  const groups = new Map<string, CRMKontaktBeteiligung[]>();
  for (const row of list) {
    const key = row.business_unit || 'sonstige';
    if (!groups.has(key)) groups.set(key, []);
    groups.get(key)!.push(row);
  }
  return Array.from(groups.entries())
    .map(([business_unit, items]) => ({
      business_unit,
      items: items
        .slice()
        .sort((a, b) => {
          const da = a.sortier_datum ? new Date(a.sortier_datum).getTime() : 0;
          const db = b.sortier_datum ? new Date(b.sortier_datum).getTime() : 0;
          return db - da;
        }),
    }))
    .sort((a, b) => b.items.length - a.items.length);
}

// ============================================================
// MAIN COMPONENT
// ============================================================

export default function KontaktDetail({ kontakt, onClose, onUpdate, onToggleFavorit, onDelete, onFindDuplicates, onMerge, onSelectKontakt }: {
  kontakt: CRMKontakt;
  onClose: () => void;
  onUpdate: (id: string, updates: Partial<CRMKontakt>) => Promise<any>;
  onToggleFavorit: (id: string) => Promise<void>;
  onDelete?: (id: string) => Promise<void>;
  onFindDuplicates?: (kontakt: CRMKontakt) => Promise<CRMKontakt[]>;
  onMerge?: (primaryId: string, secondaryIds: string[]) => Promise<any>;
  onSelectKontakt?: (kontakt: CRMKontakt) => void;
}) {
  const [activeTab, setActiveTab] = useState<DetailTab>('uebersicht');
  const [isEditing, setIsEditing] = useState(false);
  const [editForm, setEditForm] = useState<Partial<CRMKontakt>>({});
  const [saving, setSaving] = useState(false);
  const [editLabels, setEditLabels] = useState<KontaktLabel[]>([]);
  const [editBetriebe, setEditBetriebe] = useState<BetriebSchluessel[]>([]);
  const [editPrimaererBetrieb, setEditPrimaererBetrieb] = useState<BetriebSchluessel | null>(null);

  // Data hooks
  const { kommunikation, loading: komLoading } = useCRMKommunikation(kontakt.id);
  const { firmen, loading: firmenLoading } = useCRMFirmen();
  const { aktivitaeten, loading: aktLoading, create: createAktivitaet } = useCRMAktivitaeten(kontakt.id);
  const { beteiligungen, loading: beteiligungenLoading } = useCRMKontaktBeteiligungen(kontakt.id);
  const { connectedEmail, sendEmail, connect } = useGmail();
  const [, setLocation] = useLocation();
  const beteiligungenGruppen = beteiligungenNachBetrieb(beteiligungen);

  // Compose state
  const [showCompose, setShowCompose] = useState(false);
  const [composeSubject, setComposeSubject] = useState('');
  const [composeBody, setComposeBody] = useState('');
  const [sending, setSending] = useState(false);

  // Note state
  const [newNote, setNewNote] = useState('');

  // Duplikat-Erkennung
  const [duplicates, setDuplicates] = useState<CRMKontakt[]>([]);
  const [dupeLoading, setDupeLoading] = useState(false);
  const [showMergeDialog, setShowMergeDialog] = useState(false);
  const [mergeTarget, setMergeTarget] = useState<CRMKontakt | null>(null);
  const [merging, setMerging] = useState(false);
  const [dupeDismissed, setDupeDismissed] = useState(false);

  // Duplikate automatisch suchen wenn Kontakt geöffnet wird
  useEffect(() => {
    if (onFindDuplicates && kontakt.id) {
      setDupeLoading(true);
      setDupeDismissed(false);
      onFindDuplicates(kontakt).then(dupes => {
        setDuplicates(dupes);
      }).catch(() => {}).finally(() => setDupeLoading(false));
    }
  }, [kontakt.id, onFindDuplicates]);

  const handleMerge = async (primaryId: string, secondaryId: string) => {
    if (!onMerge) return;
    setMerging(true);
    try {
      await onMerge(primaryId, [secondaryId]);
      toast.success('Kontakte erfolgreich zusammengef\u00fchrt');
      setShowMergeDialog(false);
      setMergeTarget(null);
      setDuplicates(prev => prev.filter(d => d.id !== secondaryId));
    } catch (e: any) {
      toast.error(`Fehler: ${e.message}`);
    } finally {
      setMerging(false);
    }
  };

  const status = kontakt.status || 'aktiv';
  const statusCfg = STATUS_CONFIG[status] || STATUS_CONFIG.aktiv;

  // Zugehörige Firmen
  const zugehoerigeFirmen = firmen.filter(f => kontakt.firmen_ids?.includes(f.id));

  // Combined timeline (Kommunikation + Aktivitäten)
  const timeline = [
    ...kommunikation.map(k => ({
      id: k.id,
      typ: k.typ || 'email',
      titel: k.betreff || 'Ohne Betreff',
      beschreibung: k.inhalt,
      datum: k.datum,
      richtung: k.richtung,
      source: 'kommunikation' as const,
    })),
    ...aktivitaeten.map(a => ({
      id: a.id,
      typ: a.typ,
      titel: a.titel,
      beschreibung: a.beschreibung,
      datum: a.erstellt_am,
      richtung: undefined,
      source: 'aktivitaet' as const,
    })),
  ].sort((a, b) => new Date(b.datum).getTime() - new Date(a.datum).getTime());

  // Edit handlers
  const startEdit = () => {
    setEditForm({
      name: kontakt.name,
      firma: kontakt.firma,
      email: kontakt.email,
      telefon: kontakt.telefon,
      mobil: kontakt.mobil,
      adresse: kontakt.adresse,
      plz: kontakt.plz,
      ort: kontakt.ort,
      position: kontakt.position,
      webseite: kontakt.webseite,
      status: kontakt.status || 'aktiv',
    });
    setEditLabels([]);
    setEditBetriebe([]);
    setEditPrimaererBetrieb(null);
    setIsEditing(true);

    const baseOpts = { method: 'POST', headers: { 'Content-Type': 'application/json' } };
    fetch('/api/kontakt-labels', {
      ...baseOpts,
      body: JSON.stringify({ action: 'getLabels', kontakt_id: kontakt.id }),
    })
      .then((r) => r.json())
      .then((data) => {
        const labels = (data.labels ?? []).map((l: any) =>
          typeof l === 'string' ? l : l.label,
        ) as KontaktLabel[];
        setEditLabels(labels);
      })
      .catch(() => {});

    fetch('/api/kontakt-labels', {
      ...baseOpts,
      body: JSON.stringify({ action: 'getBetriebe', kontakt_id: kontakt.id }),
    })
      .then((r) => r.json())
      .then((data) => {
        const rows: Array<{ betrieb_schluessel: string; primaerer_betrieb: boolean }> =
          data.betriebe ?? [];
        setEditBetriebe(rows.map((b) => b.betrieb_schluessel as BetriebSchluessel));
        const primaer = rows.find((b) => b.primaerer_betrieb);
        setEditPrimaererBetrieb(primaer ? (primaer.betrieb_schluessel as BetriebSchluessel) : null);
      })
      .catch(() => {});
  };

  const saveEdit = async () => {
    setSaving(true);
    try {
      await onUpdate(kontakt.id, editForm);

      const baseOpts = { method: 'POST', headers: { 'Content-Type': 'application/json' } };
      const labelsRes = await fetch('/api/kontakt-labels', {
        ...baseOpts,
        body: JSON.stringify({ action: 'setLabels', kontakt_id: kontakt.id, labels: editLabels }),
      });
      if (!labelsRes.ok) {
        toast.error('Labels konnten nicht gespeichert werden');
      }
      const betriebeRes = await fetch('/api/kontakt-labels', {
        ...baseOpts,
        body: JSON.stringify({
          action: 'setBetriebe',
          kontakt_id: kontakt.id,
          betriebe: editBetriebe,
          primaerer_betrieb: editPrimaererBetrieb,
        }),
      });
      if (!betriebeRes.ok) {
        toast.error('Betriebs-Zuordnung konnte nicht gespeichert werden');
      }

      toast.success('Kontakt aktualisiert');
      setIsEditing(false);
    } catch (e: any) {
      toast.error(`Fehler: ${e.message}`);
    } finally {
      setSaving(false);
    }
  };

  // Send email
  const handleSendEmail = async () => {
    if (!kontakt.email || !composeSubject.trim()) return;
    setSending(true);
    try {
      await sendEmail(kontakt.email, composeSubject, composeBody);
      toast.success('E-Mail gesendet');
      setShowCompose(false);
      setComposeSubject('');
      setComposeBody('');
      // Log activity
      try {
        await createAktivitaet({
          kontakt_id: kontakt.id,
          typ: 'email',
          titel: `E-Mail gesendet: ${composeSubject}`,
          beschreibung: composeBody.slice(0, 200),
        });
      } catch {}
    } catch (e: any) {
      toast.error(`Fehler: ${e.message}`);
    } finally {
      setSending(false);
    }
  };

  // Add note
  const handleAddNote = async () => {
    if (!newNote.trim()) return;
    try {
      await createAktivitaet({
        kontakt_id: kontakt.id,
        typ: 'notiz',
        titel: 'Notiz hinzugefügt',
        beschreibung: newNote,
      });
      // Also update contact notes
      const existingNotes = kontakt.notizen || '';
      const timestamp = new Date().toLocaleDateString('de-DE', { day: '2-digit', month: '2-digit', year: 'numeric', hour: '2-digit', minute: '2-digit' });
      await onUpdate(kontakt.id, { notizen: `[${timestamp}] ${newNote}\n\n${existingNotes}`.trim() });
      setNewNote('');
      toast.success('Notiz gespeichert');
    } catch (e: any) {
      toast.error(`Fehler: ${e.message}`);
    }
  };

  const TABS: { id: DetailTab; label: string; icon: typeof Users; count?: number }[] = [
    { id: 'uebersicht', label: 'Übersicht', icon: Users },
    { id: 'timeline', label: 'Timeline', icon: Activity, count: timeline.length },
    { id: 'beteiligungen', label: 'Beteiligungen', icon: Folder, count: beteiligungen.length },
    { id: 'emails', label: 'E-Mails', icon: Mail, count: kommunikation.filter(k => k.typ === 'email').length },
    { id: 'firmen', label: 'Firmen', icon: Building2, count: zugehoerigeFirmen.length },
    { id: 'dokumente', label: 'Dokumente', icon: FileText },
    { id: 'notizen', label: 'Notizen', icon: MessageSquare },
  ];

  return (
    <div className="fixed inset-0 z-[60] bg-white lg:relative lg:inset-auto lg:z-auto lg:w-full lg:max-w-[480px] lg:flex-shrink-0 lg:border lg:border-gray-200 lg:rounded-xl flex flex-col overflow-hidden">
      {/* Header */}
      <div className="flex-shrink-0 border-b border-gray-200 bg-white">
        {/* Top bar */}
        <div className="flex items-center justify-between px-4 py-3">
          <button onClick={onClose} className="flex items-center gap-1 text-sm text-gray-500 hover:text-gray-700">
            <ChevronLeft className="w-5 h-5" />
            <span className="lg:hidden">Zurück</span>
          </button>
          <div className="flex items-center gap-1">
            <button onClick={() => onToggleFavorit(kontakt.id)} className="p-2 hover:bg-gray-100 rounded-lg">
              {kontakt.favorit ? <Star className="w-4 h-4 text-amber-500 fill-amber-500" /> : <Star className="w-4 h-4 text-gray-300" />}
            </button>
            {isEditing ? (
              <button onClick={saveEdit} disabled={saving} className="flex items-center gap-1 px-3 py-1.5 bg-[#C41E3A] text-white rounded-lg text-xs font-medium">
                {saving ? <Loader2 className="w-3 h-3 animate-spin" /> : <Save className="w-3 h-3" />}
                Speichern
              </button>
            ) : (
              <button onClick={startEdit} className="p-2 hover:bg-gray-100 rounded-lg">
                <Pencil className="w-4 h-4 text-gray-400" />
              </button>
            )}
            <button onClick={onClose} className="p-2 hover:bg-gray-100 rounded-lg hidden lg:flex">
              <X className="w-4 h-4 text-gray-400" />
            </button>
          </div>
        </div>

        {/* Contact Info */}
        <div className="px-3 sm:px-4 pb-2 sm:pb-3">
          <div className="flex items-center gap-2.5 sm:gap-3 mb-2 sm:mb-3">
            <div className="w-11 h-11 sm:w-14 sm:h-14 rounded-full flex items-center justify-center text-white text-base sm:text-xl font-bold bg-[#1B3A5C] flex-shrink-0">
              {getInitials(kontakt.name?.replace(/^"+|"+$/g, '') || 'UN')}
            </div>
            <div className="flex-1 min-w-0">
              {isEditing ? (
                <input value={editForm.name || ''} onChange={e => setEditForm(f => ({ ...f, name: e.target.value }))}
                  className="text-lg font-bold text-gray-900 w-full border-b border-gray-300 focus:border-[#C41E3A] focus:outline-none pb-0.5" />
              ) : (
                <h2 className="text-base sm:text-lg font-bold text-gray-900 truncate">{kontakt.name?.replace(/^"+|"+$/g, '').trim() || 'Unbenannt'}</h2>
              )}
              {isEditing ? (
                <input value={editForm.position || ''} onChange={e => setEditForm(f => ({ ...f, position: e.target.value }))}
                  className="text-xs text-gray-500 w-full border-b border-gray-200 focus:border-[#C41E3A] focus:outline-none mt-0.5" placeholder="Position/Titel" />
              ) : (
                kontakt.position && <p className="text-[11px] sm:text-xs text-gray-500">{kontakt.position}</p>
              )}
              {kontakt.firma && !isEditing && <p className="text-[11px] sm:text-xs text-gray-400">{kontakt.firma?.replace(/^"+|"+$/g, '')}</p>}
            </div>
          </div>

          {/* Status + Tags */}
          <div className="flex items-center gap-2 flex-wrap">
            <span className="text-[10px] font-semibold px-2 py-0.5 rounded-full" style={{ backgroundColor: statusCfg.bg, color: statusCfg.color }}>
              {statusCfg.label}
            </span>
            {kontakt.tags?.map(tag => (
              <span key={tag} className="text-[10px] px-2 py-0.5 rounded-full bg-gray-100 text-gray-600">{tag}</span>
            ))}
          </div>
        </div>

        {/* Duplikat-Banner */}
        {!dupeDismissed && duplicates.length > 0 && (
          <div className="mx-3 sm:mx-4 mb-2 p-2.5 bg-amber-50 border border-amber-200 rounded-lg">
            <div className="flex items-center justify-between mb-1.5">
              <div className="flex items-center gap-1.5">
                <AlertCircle className="w-3.5 h-3.5 text-amber-600" />
                <span className="text-[11px] font-semibold text-amber-800">
                  {duplicates.length} m\u00f6gliche{duplicates.length === 1 ? 's' : ''} Duplikat{duplicates.length !== 1 ? 'e' : ''}
                </span>
              </div>
              <button onClick={() => setDupeDismissed(true)} className="text-amber-400 hover:text-amber-600">
                <X className="w-3.5 h-3.5" />
              </button>
            </div>
            <div className="space-y-1.5">
              {duplicates.slice(0, 3).map(dupe => (
                <div key={dupe.id} className="flex items-center justify-between bg-white rounded-md p-2 border border-amber-100">
                  <div className="flex-1 min-w-0">
                    <p className="text-[11px] font-medium text-gray-800 truncate">{dupe.name}</p>
                    <p className="text-[10px] text-gray-500 truncate">
                      {[dupe.email, dupe.telefon, dupe.firma].filter(Boolean).join(' \u2022 ')}
                    </p>
                  </div>
                  <div className="flex items-center gap-1 ml-2 flex-shrink-0">
                    {onSelectKontakt && (
                      <button
                        onClick={() => onSelectKontakt(dupe)}
                        className="text-[9px] px-2 py-1 bg-gray-100 text-gray-600 rounded hover:bg-gray-200 transition-colors"
                      >
                        Anzeigen
                      </button>
                    )}
                    {onMerge && (
                      <button
                        onClick={() => { setMergeTarget(dupe); setShowMergeDialog(true); }}
                        className="text-[9px] px-2 py-1 bg-amber-100 text-amber-700 rounded hover:bg-amber-200 transition-colors font-medium"
                      >
                        Zusammenf\u00fchren
                      </button>
                    )}
                  </div>
                </div>
              ))}
              {duplicates.length > 3 && (
                <p className="text-[10px] text-amber-600 text-center">+{duplicates.length - 3} weitere Duplikate</p>
              )}
            </div>
          </div>
        )}

        {/* Merge Dialog */}
        {showMergeDialog && mergeTarget && (
          <div className="fixed inset-0 z-[70] bg-black/50 flex items-center justify-center p-4">
            <div className="bg-white rounded-xl shadow-2xl w-full max-w-lg max-h-[80vh] overflow-y-auto">
              <div className="p-4 border-b border-gray-200">
                <div className="flex items-center justify-between">
                  <h3 className="text-sm font-bold text-gray-900">Kontakte zusammenf\u00fchren</h3>
                  <button onClick={() => { setShowMergeDialog(false); setMergeTarget(null); }} className="p-1 hover:bg-gray-100 rounded">
                    <X className="w-4 h-4 text-gray-400" />
                  </button>
                </div>
                <p className="text-[11px] text-gray-500 mt-1">W\u00e4hle welcher Kontakt beibehalten wird. Fehlende Daten werden erg\u00e4nzt.</p>
              </div>
              <div className="p-4">
                <div className="grid grid-cols-2 gap-3 mb-4">
                  {/* Primary */}
                  <div className="p-3 border-2 border-green-300 bg-green-50 rounded-lg">
                    <div className="text-[10px] font-semibold text-green-700 uppercase mb-2">Behalten (Prim\u00e4r)</div>
                    <p className="text-xs font-bold text-gray-900 truncate">{kontakt.name}</p>
                    {kontakt.firma && <p className="text-[10px] text-gray-500 truncate">{kontakt.firma}</p>}
                    {kontakt.email && <p className="text-[10px] text-blue-600 truncate">{kontakt.email}</p>}
                    {kontakt.telefon && <p className="text-[10px] text-gray-600">{kontakt.telefon}</p>}
                    <p className="text-[9px] text-gray-400 mt-1">Kat: {kontakt.kategorie}</p>
                  </div>
                  {/* Secondary */}
                  <div className="p-3 border-2 border-red-200 bg-red-50 rounded-lg">
                    <div className="text-[10px] font-semibold text-red-600 uppercase mb-2">L\u00f6schen (Sekund\u00e4r)</div>
                    <p className="text-xs font-bold text-gray-900 truncate">{mergeTarget.name}</p>
                    {mergeTarget.firma && <p className="text-[10px] text-gray-500 truncate">{mergeTarget.firma}</p>}
                    {mergeTarget.email && <p className="text-[10px] text-blue-600 truncate">{mergeTarget.email}</p>}
                    {mergeTarget.telefon && <p className="text-[10px] text-gray-600">{mergeTarget.telefon}</p>}
                    <p className="text-[9px] text-gray-400 mt-1">Kat: {mergeTarget.kategorie}</p>
                  </div>
                </div>
                {/* Field comparison */}
                <div className="text-[10px] font-semibold text-gray-500 uppercase mb-2">Feld-Vergleich</div>
                <div className="space-y-1 mb-4">
                  {(['name', 'email', 'telefon', 'mobil', 'firma', 'adresse', 'ort', 'kategorie'] as const).map(field => {
                    const pVal = (kontakt as any)[field] || '';
                    const sVal = (mergeTarget as any)[field] || '';
                    const match = pVal === sVal;
                    const willMerge = !pVal && sVal;
                    return (
                      <div key={field} className={`flex items-center gap-2 px-2 py-1 rounded text-[10px] ${
                        willMerge ? 'bg-blue-50' : match ? 'bg-gray-50' : 'bg-white'
                      }`}>
                        <span className="w-16 font-medium text-gray-500 capitalize flex-shrink-0">{field}</span>
                        <span className="flex-1 truncate text-gray-800">{pVal || <span className="text-gray-300">-</span>}</span>
                        <span className="text-gray-300">|</span>
                        <span className={`flex-1 truncate ${willMerge ? 'text-blue-600 font-medium' : 'text-gray-500'}`}>
                          {sVal || <span className="text-gray-300">-</span>}
                          {willMerge && <span className="text-blue-500 ml-1">\u2190 wird \u00fcbernommen</span>}
                        </span>
                      </div>
                    );
                  })}
                </div>
              </div>
              <div className="p-4 border-t border-gray-200 flex items-center justify-between gap-2">
                <button
                  onClick={() => { setShowMergeDialog(false); setMergeTarget(null); }}
                  className="px-4 py-2 text-xs text-gray-600 hover:bg-gray-100 rounded-lg transition-colors"
                >
                  Abbrechen
                </button>
                <div className="flex gap-2">
                  <button
                    onClick={() => handleMerge(mergeTarget.id, kontakt.id)}
                    className="px-3 py-2 text-[10px] bg-gray-100 text-gray-700 rounded-lg hover:bg-gray-200 transition-colors"
                  >
                    Tauschen &amp; Zusammenf\u00fchren
                  </button>
                  <button
                    onClick={() => handleMerge(kontakt.id, mergeTarget.id)}
                    disabled={merging}
                    className="px-4 py-2 text-xs bg-[#C41E3A] text-white rounded-lg hover:bg-[#A31830] transition-colors disabled:opacity-50 font-medium"
                  >
                    {merging ? 'Wird zusammengef\u00fchrt...' : 'Zusammenf\u00fchren'}
                  </button>
                </div>
              </div>
            </div>
          </div>
        )}

        {/* Quick Actions */}
        <div className="grid grid-cols-4 gap-1 px-3 sm:px-4 pb-2 sm:pb-3">
          <button
            onClick={() => {
              const phoneNum = kontakt.telefon || kontakt.mobil;
              if (phoneNum) {
                window.dispatchEvent(new CustomEvent('koop:make-call', { detail: { number: phoneNum, name: kontakt.name } }));
              }
            }}
            className="flex flex-col items-center gap-1 py-2 rounded-lg bg-blue-50 hover:bg-blue-100 transition-colors"
          >
            <Phone className="w-4 h-4 text-blue-600" />
            <span className="text-[9px] font-medium text-blue-700">Anrufen</span>
          </button>
          <button onClick={() => { setActiveTab('emails'); setShowCompose(true); }} className="flex flex-col items-center gap-1 py-2 rounded-lg bg-purple-50 hover:bg-purple-100 transition-colors">
            <Mail className="w-4 h-4 text-purple-600" />
            <span className="text-[9px] font-medium text-purple-700">E-Mail</span>
          </button>
          <button onClick={() => setActiveTab('notizen')} className="flex flex-col items-center gap-1 py-2 rounded-lg bg-amber-50 hover:bg-amber-100 transition-colors">
            <MessageSquare className="w-4 h-4 text-amber-600" />
            <span className="text-[9px] font-medium text-amber-700">Notiz</span>
          </button>
          <button className="flex flex-col items-center gap-1 py-2 rounded-lg bg-green-50 hover:bg-green-100 transition-colors">
            <Calendar className="w-4 h-4 text-green-600" />
            <span className="text-[9px] font-medium text-green-700">Termin</span>
          </button>
        </div>

        {/* Tabs */}
        <div className="flex gap-0 overflow-x-auto px-2 sm:px-4 border-t border-gray-100" style={{ scrollbarWidth: 'none', msOverflowStyle: 'none' }}>
          {TABS.map(tab => (
            <button
              key={tab.id}
              onClick={() => setActiveTab(tab.id)}
              className={`flex items-center gap-0.5 sm:gap-1 px-2 sm:px-3 py-2 sm:py-2.5 text-[10px] sm:text-[11px] font-medium whitespace-nowrap border-b-2 transition-colors ${
                activeTab === tab.id ? 'border-[#C41E3A] text-[#C41E3A]' : 'border-transparent text-gray-500 hover:text-gray-700'
              }`}
            >
              <tab.icon className="w-3 h-3 hidden sm:block" />
              {tab.label}
              {tab.count !== undefined && tab.count > 0 && (
                <span className="text-[8px] sm:text-[9px] px-1 py-0 rounded-full bg-gray-100 text-gray-500 ml-0.5">{tab.count}</span>
              )}
            </button>
          ))}
        </div>
      </div>

      {/* Tab Content */}
      <div className="flex-1 overflow-y-auto p-4">
        {/* ÜBERSICHT */}
        {activeTab === 'uebersicht' && (
          <div className="space-y-5">
            {/* Kontaktdaten */}
            <div>
              <h4 className="text-xs font-semibold text-gray-500 uppercase tracking-wide mb-3">Kontaktdaten</h4>
              <div className="space-y-2.5">
                {isEditing ? (
                  <div className="space-y-2">
                    <div className="grid grid-cols-2 gap-2">
                      <input value={editForm.email || ''} onChange={e => setEditForm(f => ({ ...f, email: e.target.value }))}
                        className="px-3 py-2 border border-gray-200 rounded-lg text-sm" placeholder="E-Mail" />
                      <input value={editForm.telefon || ''} onChange={e => setEditForm(f => ({ ...f, telefon: e.target.value }))}
                        className="px-3 py-2 border border-gray-200 rounded-lg text-sm" placeholder="Telefon" />
                    </div>
                    <div className="grid grid-cols-2 gap-2">
                      <input value={editForm.mobil || ''} onChange={e => setEditForm(f => ({ ...f, mobil: e.target.value }))}
                        className="px-3 py-2 border border-gray-200 rounded-lg text-sm" placeholder="Mobil" />
                      <input value={editForm.firma || ''} onChange={e => setEditForm(f => ({ ...f, firma: e.target.value }))}
                        className="px-3 py-2 border border-gray-200 rounded-lg text-sm" placeholder="Firma" />
                    </div>
                    <input value={editForm.adresse || ''} onChange={e => setEditForm(f => ({ ...f, adresse: e.target.value }))}
                      className="w-full px-3 py-2 border border-gray-200 rounded-lg text-sm" placeholder="Adresse" />
                    <div className="grid grid-cols-1 sm:grid-cols-3 gap-2">
                      <input value={editForm.plz || ''} onChange={e => setEditForm(f => ({ ...f, plz: e.target.value }))}
                        className="px-3 py-2 border border-gray-200 rounded-lg text-sm" placeholder="PLZ" />
                      <input value={editForm.ort || ''} onChange={e => setEditForm(f => ({ ...f, ort: e.target.value }))}
                        className="col-span-2 px-3 py-2 border border-gray-200 rounded-lg text-sm" placeholder="Ort" />
                    </div>
                    <input value={editForm.webseite || ''} onChange={e => setEditForm(f => ({ ...f, webseite: e.target.value }))}
                      className="w-full px-3 py-2 border border-gray-200 rounded-lg text-sm" placeholder="Webseite" />
                    <select value={editForm.status || 'aktiv'} onChange={e => setEditForm(f => ({ ...f, status: e.target.value }))}
                      className="w-full px-3 py-2 border border-gray-200 rounded-lg text-sm">
                      <option value="aktiv">Aktiv</option>
                      <option value="inaktiv">Inaktiv</option>
                      <option value="lead">Lead</option>
                      <option value="kunde">Kunde</option>
                    </select>
                    <div>
                      <p className="text-xs text-gray-500 mb-1">Kategorie-Labels</p>
                      <LabelPicker selectedLabels={editLabels} onChange={setEditLabels} />
                    </div>
                    <div>
                      <p className="text-xs text-gray-500 mb-1">Betriebs-Zuordnung</p>
                      <BetriebePicker
                        selectedBetriebe={editBetriebe}
                        primaererBetrieb={editPrimaererBetrieb}
                        onBetriebeChange={setEditBetriebe}
                        onPrimaererBetriebeChange={setEditPrimaererBetrieb}
                      />
                    </div>
                  </div>
                ) : (
                  <>
                    {kontakt.email && (
                      <div className="flex items-center gap-3">
                        <Mail className="w-4 h-4 text-gray-400 flex-shrink-0" />
                        <a href={`mailto:${kontakt.email}`} className="text-sm text-blue-600 hover:underline truncate">{kontakt.email}</a>
                      </div>
                    )}
                    {kontakt.telefon && (
                      <div className="flex items-center gap-3">
                        <Phone className="w-4 h-4 text-gray-400 flex-shrink-0" />
                        <a href={`tel:${kontakt.telefon}`} className="text-sm text-gray-700">{kontakt.telefon}</a>
                      </div>
                    )}
                    {kontakt.mobil && (
                      <div className="flex items-center gap-3">
                        <Phone className="w-4 h-4 text-gray-400 flex-shrink-0" />
                        <span className="text-sm text-gray-700">{kontakt.mobil} <span className="text-gray-400">(Mobil)</span></span>
                      </div>
                    )}
                    {(kontakt.adresse || kontakt.plz || kontakt.ort) && (
                      <div className="flex items-start gap-3">
                        <MapPin className="w-4 h-4 text-gray-400 flex-shrink-0 mt-0.5" />
                        <div className="text-sm text-gray-700">
                          {kontakt.adresse && <div>{kontakt.adresse}</div>}
                          {(kontakt.plz || kontakt.ort) && <div>{[kontakt.plz, kontakt.ort].filter(Boolean).join(' ')}</div>}
                        </div>
                      </div>
                    )}
                    {kontakt.webseite && (
                      <div className="flex items-center gap-3">
                        <Globe className="w-4 h-4 text-gray-400 flex-shrink-0" />
                        <a href={kontakt.webseite.startsWith('http') ? kontakt.webseite : `https://${kontakt.webseite}`} target="_blank" rel="noopener" className="text-sm text-blue-600 hover:underline">{kontakt.webseite}</a>
                      </div>
                    )}
                    {kontakt.letzter_kontakt && (
                      <div className="flex items-center gap-3">
                        <Clock className="w-4 h-4 text-gray-400 flex-shrink-0" />
                        <span className="text-sm text-gray-500">Letzter Kontakt: {formatDate(kontakt.letzter_kontakt)}</span>
                      </div>
                    )}
                  </>
                )}
              </div>
            </div>

            {/* Zugehörige Firmen */}
            {zugehoerigeFirmen.length > 0 && (
              <div>
                <h4 className="text-xs font-semibold text-gray-500 uppercase tracking-wide mb-3">Firmen</h4>
                <div className="space-y-2">
                  {zugehoerigeFirmen.map(firma => (
                    <div key={firma.id} className="flex items-center gap-3 p-3 rounded-lg bg-gray-50 border border-gray-100">
                      <div className="w-9 h-9 rounded-lg bg-[#1B3A5C] flex items-center justify-center text-white text-xs font-bold flex-shrink-0">
                        {firma.name.slice(0, 2).toUpperCase()}
                      </div>
                      <div className="flex-1 min-w-0">
                        <p className="text-sm font-semibold text-gray-900 truncate">{firma.name}</p>
                        {firma.branche && <p className="text-[10px] text-gray-500">{firma.branche}</p>}
                      </div>
                      <ArrowUpRight className="w-4 h-4 text-gray-300" />
                    </div>
                  ))}
                </div>
              </div>
            )}

            {/* Letzte Aktivitäten */}
            <div>
              <h4 className="text-xs font-semibold text-gray-500 uppercase tracking-wide mb-3">Letzte Aktivitäten</h4>
              {timeline.length === 0 ? (
                <p className="text-xs text-gray-400 text-center py-4">Noch keine Aktivitäten</p>
              ) : (
                <div className="space-y-2">
                  {timeline.slice(0, 5).map(item => {
                    const cfg = ACTIVITY_ICONS[item.typ] || ACTIVITY_ICONS.notiz;
                    const Icon = cfg.icon;
                    return (
                      <div key={item.id} className="flex items-start gap-3 p-2.5 rounded-lg hover:bg-gray-50 transition-colors">
                        <div className="w-8 h-8 rounded-lg flex items-center justify-center flex-shrink-0" style={{ backgroundColor: cfg.bg }}>
                          <Icon className="w-4 h-4" style={{ color: cfg.color }} />
                        </div>
                        <div className="flex-1 min-w-0">
                          <p className="text-xs font-medium text-gray-800 truncate">{item.titel}</p>
                          {item.beschreibung && <p className="text-[10px] text-gray-500 truncate mt-0.5">{item.beschreibung}</p>}
                          <p className="text-[10px] text-gray-400 mt-0.5">
                            {item.richtung === 'eingehend' ? '← ' : item.richtung === 'ausgehend' ? '→ ' : ''}
                            {timeAgo(item.datum)}
                          </p>
                        </div>
                      </div>
                    );
                  })}
                  {timeline.length > 5 && (
                    <button onClick={() => setActiveTab('timeline')} className="w-full text-center text-xs text-[#C41E3A] font-medium py-2 hover:bg-red-50 rounded-lg transition-colors">
                      Alle {timeline.length} Aktivitäten anzeigen
                    </button>
                  )}
                </div>
              )}
            </div>

            {/* Metadaten */}
            <div className="pt-3 border-t border-gray-100">
              <div className="grid grid-cols-2 gap-3 text-[10px] text-gray-400">
                <div>Erstellt: {formatDate(kontakt.erstellt_am)}</div>
                <div>Aktualisiert: {formatDate(kontakt.aktualisiert_am)}</div>
                {kontakt.quelle && <div>Quelle: {kontakt.quelle}</div>}
                <div>ID: {kontakt.id.slice(0, 8)}...</div>
              </div>
            </div>
          </div>
        )}

        {/* TIMELINE */}
        {activeTab === 'timeline' && (
          <div>
            <div className="flex items-center justify-between mb-4">
              <h3 className="text-sm font-bold text-gray-900">Aktivitäts-Timeline</h3>
              <span className="text-xs text-gray-400">{timeline.length} Einträge</span>
            </div>
            {(komLoading || aktLoading) ? (
              <div className="flex justify-center py-8"><Loader2 className="w-5 h-5 text-gray-400 animate-spin" /></div>
            ) : timeline.length === 0 ? (
              <div className="text-center py-8">
                <Activity className="w-8 h-8 text-gray-300 mx-auto mb-2" />
                <p className="text-sm text-gray-400">Noch keine Aktivitäten</p>
              </div>
            ) : (
              <div className="relative">
                {/* Timeline line */}
                <div className="absolute left-4 top-0 bottom-0 w-px bg-gray-200" />
                <div className="space-y-0">
                  {timeline.map((item, idx) => {
                    const cfg = ACTIVITY_ICONS[item.typ] || ACTIVITY_ICONS.notiz;
                    const Icon = cfg.icon;
                    return (
                      <div key={item.id} className="relative flex items-start gap-3 py-3 pl-1">
                        <div className="w-8 h-8 rounded-full flex items-center justify-center flex-shrink-0 bg-white border-2 border-gray-200 z-10" style={{ borderColor: cfg.color }}>
                          <Icon className="w-3.5 h-3.5" style={{ color: cfg.color }} />
                        </div>
                        <div className="flex-1 min-w-0 pt-0.5">
                          <div className="flex items-center gap-2">
                            <p className="text-xs font-semibold text-gray-800">{item.titel}</p>
                            {item.richtung && (
                              <span className="text-[9px] px-1.5 py-0.5 rounded bg-gray-100 text-gray-500">
                                {item.richtung === 'eingehend' ? '← Eingehend' : '→ Ausgehend'}
                              </span>
                            )}
                          </div>
                          {item.beschreibung && (
                            <p className="text-[11px] text-gray-600 mt-1 line-clamp-2">{item.beschreibung}</p>
                          )}
                          <p className="text-[10px] text-gray-400 mt-1">{formatDateTime(item.datum)}</p>
                        </div>
                      </div>
                    );
                  })}
                </div>
              </div>
            )}
          </div>
        )}

        {/* BETEILIGUNGEN (Issue #337) */}
        {activeTab === 'beteiligungen' && (
          <div>
            <div className="flex items-center justify-between mb-4">
              <h3 className="text-sm font-bold text-gray-900">Beteiligungen</h3>
              <span className="text-xs text-gray-400">{beteiligungen.length} {beteiligungen.length === 1 ? 'Fall' : 'Fälle'}</span>
            </div>
            {beteiligungenLoading ? (
              <div className="flex justify-center py-8"><Loader2 className="w-5 h-5 text-gray-400 animate-spin" /></div>
            ) : beteiligungen.length === 0 ? (
              <div className="text-center py-8">
                <Folder className="w-8 h-8 text-gray-300 mx-auto mb-2" />
                <p className="text-sm text-gray-400">Keine Akten mit diesem Kontakt verknüpft</p>
              </div>
            ) : (
              <div className="space-y-5">
                {beteiligungenGruppen.map(gruppe => (
                  <div key={gruppe.business_unit}>
                    <div className="flex items-center gap-2 mb-2">
                      <h4 className="text-xs font-semibold text-gray-600 uppercase tracking-wide">
                        {BUSINESS_UNIT_LABEL[gruppe.business_unit] || gruppe.business_unit}
                      </h4>
                      <span className="text-[10px] px-1.5 py-0.5 rounded-full bg-gray-100 text-gray-600">
                        {gruppe.items.length}
                      </span>
                      <div className="flex-1 border-t border-gray-200 ml-1" />
                    </div>
                    <div className="space-y-1.5">
                      {gruppe.items.map(item => {
                        const ident = aktenIdentifier({
                          akten_nummer: item.akten_nummer,
                          akten_nr: item.akten_nr,
                        });
                        const goToAkte = () => {
                          if (!ident) return;
                          setLocation(buildAkteHref(item.business_unit, ident, 'uebersicht'));
                        };
                        return (
                          <button
                            key={`${item.akte_id}-${item.rolle}-${item.quelle}`}
                            onClick={goToAkte}
                            disabled={!ident}
                            className="w-full flex items-center gap-3 px-3 py-2 rounded-lg bg-white border border-gray-200 hover:border-gray-300 hover:bg-gray-50 transition-colors text-left disabled:opacity-50 disabled:cursor-not-allowed disabled:hover:border-gray-200 disabled:hover:bg-white"
                          >
                            <Briefcase className="w-4 h-4 text-gray-400 flex-shrink-0" />
                            <div className="flex-1 min-w-0">
                              <div className="flex items-center gap-2">
                                <span className="text-xs font-mono font-semibold text-gray-800 truncate">
                                  {ident ?? item.akte_id.slice(0, 8)}
                                </span>
                                {item.status && (
                                  <span className="text-[9px] px-1.5 py-0.5 rounded-full bg-gray-100 text-gray-600 flex-shrink-0">
                                    {item.status}
                                  </span>
                                )}
                              </div>
                              <div className="flex items-center gap-2 mt-0.5">
                                <span className="text-[10px] text-gray-500">
                                  {item.sortier_datum ? formatDate(item.sortier_datum) : '—'}
                                </span>
                                <span className="text-gray-300">·</span>
                                <span className="text-[10px] text-gray-600 font-medium">
                                  Rolle: {ROLLE_LABEL[item.rolle] || item.rolle}
                                </span>
                              </div>
                              {item.titel && (
                                <p className="text-[10px] text-gray-400 truncate mt-0.5">{item.titel}</p>
                              )}
                            </div>
                            <ChevronRight className="w-4 h-4 text-gray-300 flex-shrink-0" />
                          </button>
                        );
                      })}
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>
        )}

        {/* E-MAILS */}
        {activeTab === 'emails' && (
          <div>
            {!connectedEmail ? (
              <div className="text-center py-8">
                <Mail className="w-8 h-8 text-gray-300 mx-auto mb-2" />
                <p className="text-sm text-gray-500 mb-3">Gmail nicht verbunden</p>
                <button onClick={() => connect('/crm/kontakte')} className="px-4 py-2 bg-[#C41E3A] text-white rounded-lg text-sm font-medium hover:bg-[#A3172F] transition-colors">
                  Gmail verbinden
                </button>
              </div>
            ) : (
              <div>
                {/* Compose */}
                <div className="flex items-center justify-between mb-4">
                  <h3 className="text-sm font-bold text-gray-900">E-Mail-Verlauf</h3>
                  <button onClick={() => setShowCompose(!showCompose)} className="flex items-center gap-1 px-3 py-1.5 bg-[#C41E3A] text-white rounded-lg text-xs font-medium hover:bg-[#A3172F] transition-colors">
                    <Send className="w-3 h-3" />
                    Neue E-Mail
                  </button>
                </div>

                {showCompose && (
                  <div className="mb-4 p-3 bg-gray-50 rounded-xl border border-gray-200 space-y-2">
                    <div className="text-xs text-gray-500">An: <span className="font-medium text-gray-700">{kontakt.email}</span></div>
                    <input value={composeSubject} onChange={e => setComposeSubject(e.target.value)}
                      className="w-full px-3 py-2 border border-gray-200 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-[#C41E3A]/20" placeholder="Betreff" />
                    <textarea value={composeBody} onChange={e => setComposeBody(e.target.value)}
                      className="w-full px-3 py-2 border border-gray-200 rounded-lg text-sm h-24 resize-none focus:outline-none focus:ring-2 focus:ring-[#C41E3A]/20" placeholder="Nachricht..." />
                    <div className="flex justify-end gap-2">
                      <button onClick={() => setShowCompose(false)} className="px-3 py-1.5 text-xs text-gray-500 hover:bg-gray-100 rounded-lg">Abbrechen</button>
                      <button onClick={handleSendEmail} disabled={sending} className="flex items-center gap-1 px-3 py-1.5 bg-[#C41E3A] text-white rounded-lg text-xs font-medium disabled:opacity-50">
                        {sending ? <Loader2 className="w-3 h-3 animate-spin" /> : <Send className="w-3 h-3" />}
                        Senden
                      </button>
                    </div>
                  </div>
                )}

                {/* Email list from Kommunikation */}
                {komLoading ? (
                  <div className="flex justify-center py-8"><Loader2 className="w-5 h-5 text-gray-400 animate-spin" /></div>
                ) : kommunikation.filter(k => k.typ === 'email').length === 0 ? (
                  <div className="text-center py-8">
                    <Mail className="w-8 h-8 text-gray-300 mx-auto mb-2" />
                    <p className="text-sm text-gray-400">Keine E-Mails mit diesem Kontakt</p>
                  </div>
                ) : (
                  <div className="space-y-2">
                    {kommunikation.filter(k => k.typ === 'email').map(email => (
                      <div key={email.id} className="p-3 rounded-xl bg-white border border-gray-200 hover:border-gray-300 transition-colors">
                        <div className="flex items-center gap-2 mb-1">
                          {email.richtung === 'eingehend' ? (
                            <ArrowDownLeft className="w-3.5 h-3.5 text-blue-500 flex-shrink-0" />
                          ) : (
                            <ArrowUpRight className="w-3.5 h-3.5 text-green-500 flex-shrink-0" />
                          )}
                          <p className="text-xs font-semibold text-gray-800 truncate flex-1">{email.betreff || 'Ohne Betreff'}</p>
                        </div>
                        {email.inhalt && <p className="text-[11px] text-gray-600 truncate ml-5.5">{email.inhalt}</p>}
                        <p className="text-[10px] text-gray-400 mt-1 ml-5.5">{formatDateTime(email.datum)}</p>
                      </div>
                    ))}
                  </div>
                )}
              </div>
            )}
          </div>
        )}

        {/* FIRMEN */}
        {activeTab === 'firmen' && (
          <div>
            <div className="flex items-center justify-between mb-4">
              <h3 className="text-sm font-bold text-gray-900">Zugehörige Firmen</h3>
              <button className="flex items-center gap-1 px-3 py-1.5 bg-[#C41E3A] text-white rounded-lg text-xs font-medium hover:bg-[#A3172F] transition-colors">
                <Plus className="w-3 h-3" />
                Firma zuordnen
              </button>
            </div>

            {firmenLoading ? (
              <div className="flex justify-center py-8"><Loader2 className="w-5 h-5 text-gray-400 animate-spin" /></div>
            ) : zugehoerigeFirmen.length === 0 ? (
              <div className="text-center py-8">
                <Building2 className="w-8 h-8 text-gray-300 mx-auto mb-2" />
                <p className="text-sm text-gray-400 mb-2">Noch keiner Firma zugeordnet</p>
                <p className="text-xs text-gray-400">Ordnen Sie diesen Kontakt einer oder mehreren Firmen zu</p>
              </div>
            ) : (
              <div className="space-y-3">
                {zugehoerigeFirmen.map(firma => (
                  <div key={firma.id} className="p-4 rounded-xl bg-white border border-gray-200">
                    <div className="flex items-center gap-3 mb-3">
                      <div className="w-10 h-10 rounded-lg bg-[#1B3A5C] flex items-center justify-center text-white text-sm font-bold flex-shrink-0">
                        {firma.name.slice(0, 2).toUpperCase()}
                      </div>
                      <div className="flex-1 min-w-0">
                        <p className="text-sm font-bold text-gray-900">{firma.name}</p>
                        {firma.branche && <p className="text-xs text-gray-500">{firma.branche}</p>}
                      </div>
                    </div>
                    <div className="space-y-1.5 text-xs text-gray-600">
                      {firma.email && <div className="flex items-center gap-2"><Mail className="w-3 h-3 text-gray-400" />{firma.email}</div>}
                      {firma.telefon && <div className="flex items-center gap-2"><Phone className="w-3 h-3 text-gray-400" />{firma.telefon}</div>}
                      {firma.ort && <div className="flex items-center gap-2"><MapPin className="w-3 h-3 text-gray-400" />{firma.plz} {firma.ort}</div>}
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>
        )}

        {/* DOKUMENTE */}
        {activeTab === 'dokumente' && (
          <div>
            <div className="flex items-center justify-between mb-4">
              <h3 className="text-sm font-bold text-gray-900">Dokumente & Anhänge</h3>
              <button className="flex items-center gap-1 px-3 py-1.5 bg-[#C41E3A] text-white rounded-lg text-xs font-medium hover:bg-[#A3172F] transition-colors">
                <Plus className="w-3 h-3" />
                Hochladen
              </button>
            </div>
            <div className="text-center py-8">
              <FileText className="w-8 h-8 text-gray-300 mx-auto mb-2" />
              <p className="text-sm text-gray-400 mb-1">Noch keine Dokumente</p>
              <p className="text-xs text-gray-400">Laden Sie Dokumente hoch oder verknüpfen Sie Akten</p>
            </div>
          </div>
        )}

        {/* NOTIZEN */}
        {activeTab === 'notizen' && (
          <div>
            <h3 className="text-sm font-bold text-gray-900 mb-4">Notizen</h3>

            {/* Neue Notiz */}
            <div className="mb-4 p-3 bg-gray-50 rounded-xl border border-gray-200">
              <textarea
                value={newNote}
                onChange={e => setNewNote(e.target.value)}
                className="w-full px-3 py-2 border border-gray-200 rounded-lg text-sm h-20 resize-none focus:outline-none focus:ring-2 focus:ring-[#C41E3A]/20 bg-white"
                placeholder="Neue Notiz schreiben..."
              />
              <div className="flex justify-end mt-2">
                <button onClick={handleAddNote} disabled={!newNote.trim()} className="flex items-center gap-1 px-3 py-1.5 bg-[#C41E3A] text-white rounded-lg text-xs font-medium disabled:opacity-50 hover:bg-[#A3172F] transition-colors">
                  <Plus className="w-3 h-3" />
                  Notiz speichern
                </button>
              </div>
            </div>

            {/* Bestehende Notizen */}
            {kontakt.notizen ? (
              <div className="p-4 bg-white rounded-xl border border-gray-200">
                <pre className="text-sm text-gray-700 whitespace-pre-wrap font-sans leading-relaxed">{kontakt.notizen}</pre>
              </div>
            ) : (
              <p className="text-xs text-gray-400 text-center py-4">Noch keine Notizen vorhanden</p>
            )}

            {/* Notiz-Aktivitäten aus Timeline */}
            {aktivitaeten.filter(a => a.typ === 'notiz').length > 0 && (
              <div className="mt-4">
                <h4 className="text-xs font-semibold text-gray-500 uppercase tracking-wide mb-3">Notiz-Verlauf</h4>
                <div className="space-y-2">
                  {aktivitaeten.filter(a => a.typ === 'notiz').map(note => (
                    <div key={note.id} className="p-3 rounded-lg bg-amber-50/50 border border-amber-100">
                      <p className="text-xs text-gray-700">{note.beschreibung}</p>
                      <p className="text-[10px] text-gray-400 mt-1">{formatDateTime(note.erstellt_am)}</p>
                    </div>
                  ))}
                </div>
              </div>
            )}
          </div>
        )}
      </div>
    </div>
  );
}
